<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import {
  definitionSha256,
  lintDefinition,
  validatePackage,
  type ClinicCrfPackageV1,
  type ValidationIssue,
} from '@clinic-crf/contract'
import { CheckCircle2, CircleAlert, Copy, Fingerprint, Lightbulb, ShieldCheck } from 'lucide-vue-next'

const props = defineProps<{ packageValue: ClinicCrfPackageV1 }>()
const hash = ref('Obliczanie…')
const copied = ref(false)

const validationIssues = computed(() => validatePackage(props.packageValue).issues)
const lintIssues = computed(() => lintDefinition(props.packageValue.definition))
const issues = computed(() => {
  const seen = new Set<string>()
  return [...validationIssues.value, ...lintIssues.value].filter((issue) => {
    const key = `${issue.path}|${issue.code}|${issue.message}`
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
})
const errors = computed(() => issues.value.filter((issue) => issue.severity === 'error'))
const warnings = computed(() => issues.value.filter((issue) => issue.severity === 'warning'))
const score = computed(() => Math.max(0, 100 - errors.value.length * 18 - warnings.value.length * 4))

watch(() => props.packageValue.definition, async (value) => {
  try { hash.value = await definitionSha256(value) } catch { hash.value = 'SHA-256 niedostępny w tej przeglądarce' }
}, { deep: true, immediate: true })

async function copyHash(): Promise<void> {
  try {
    await navigator.clipboard.writeText(hash.value)
    copied.value = true
    window.setTimeout(() => { copied.value = false }, 1500)
  } catch { copied.value = false }
}

function grouped(items: ValidationIssue[]): Record<string, ValidationIssue[]> {
  return items.reduce<Record<string, ValidationIssue[]>>((groups, item) => {
    const root = item.path.split('.').slice(0, 2).join('.') || 'pakiet'
    ;(groups[root] ??= []).push(item)
    return groups
  }, {})
}
</script>

<template>
  <aside class="quality-panel">
    <header class="quality-header">
      <div><span class="eyebrow">Walidacja ciągła</span><h2>Jakość formularza</h2></div>
      <div class="score-ring" :style="{ '--score': `${score * 3.6}deg` }" :aria-label="`Wynik jakości ${score} na 100`"><span>{{ score }}</span></div>
    </header>

    <div class="quality-summary">
      <div :class="errors.length ? 'summary-error' : 'summary-ok'"><CircleAlert v-if="errors.length" :size="19" /><CheckCircle2 v-else :size="19" /><span><strong>{{ errors.length }}</strong> błędów</span></div>
      <div><Lightbulb :size="19" /><span><strong>{{ warnings.length }}</strong> sugestii</span></div>
      <div><ShieldCheck :size="19" /><span><strong>{{ packageValue.definition.fields.length }}</strong> pól</span></div>
    </div>

    <div v-if="!issues.length" class="quality-empty"><CheckCircle2 :size="32" /><h3>Pakiet jest gotowy</h3><p>Kontrakt, prezentacja i reguły przechodzą wszystkie kontrole.</p></div>

    <div v-else class="issue-list">
      <template v-for="(group, name) in grouped(issues)" :key="name">
        <h3>{{ name }}</h3>
        <article v-for="issue in group" :key="`${issue.code}-${issue.path}`" :class="['issue', issue.severity]">
          <CircleAlert v-if="issue.severity === 'error'" :size="17" /><Lightbulb v-else :size="17" />
          <div><p>{{ issue.message }}</p><code>{{ issue.path || 'pakiet' }}</code></div>
        </article>
      </template>
    </div>

    <div class="hash-card">
      <div><Fingerprint :size="18" /><span>SHA-256 definicji</span></div>
      <code>{{ hash }}</code>
      <button class="button button-quiet button-small" type="button" @click="copyHash"><Copy :size="15" /> {{ copied ? 'Skopiowano' : 'Kopiuj hash' }}</button>
    </div>

    <div class="quality-note"><strong>Co sprawdzamy?</strong><p>Format pakietu, unikalność ścieżek, sekcje, typy, opcje, zakresy, warunki, reguły cross-field i dobre praktyki kontraktu.</p></div>
  </aside>
</template>
